import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getUserById, updateUserProfile } from '../services/userService';
import {
  COLLECTIONS,
  USERS_FIELDS,
  GROUPS_FIELDS,
  GROUP_MEMBERS_FIELDS,
  GROUP_MEMBER_STATUS,
  RATINGS_FIELDS,
} from '../domain/dataModel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';

function formatDate(it) {
  if (!it?.toDate) return '—';
  return it.toDate().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
}

function getInitials(displayName, email) {
  if (displayName && typeof displayName === 'string') {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts[0].length) return parts[0].slice(0, 2).toUpperCase();
  }
  if (email && typeof email === 'string') return email.slice(0, 2).toUpperCase();
  return '?';
}

function avatarColor(str) {
  let h = 0;
  for (let i = 0; i < (str || '').length; i++) h = (h << 5) - h + str.charCodeAt(i);
  const hue = Math.abs(h % 360);
  return `hsl(${hue}, 55%, 45%)`;
}

function IconCamera({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [userProfile, setUserProfile] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [memberships, setMemberships] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [groupsMap, setGroupsMap] = useState({});

  const avatarRef = React.useRef(null);

  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const profile = await getUserById(user.uid);
        if (cancelled) return;
        setUserProfile(profile);
        setDisplayName(profile?.[USERS_FIELDS.DISPLAY_NAME] ?? '');

        const [memSnap, ratSnap] = await Promise.all([
          getDocs(
            query(
              collection(db, COLLECTIONS.GROUP_MEMBERS),
              where(GROUP_MEMBERS_FIELDS.USER_ID, '==', user.uid),
              where(GROUP_MEMBERS_FIELDS.STATUS, '==', GROUP_MEMBER_STATUS.ACTIVE)
            )
          ),
          getDocs(
            query(
              collection(db, COLLECTIONS.RATINGS),
              where(RATINGS_FIELDS.USER_ID, '==', user.uid)
            )
          ),
        ]);
        if (cancelled) return;
        setMemberships(memSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setRatings(ratSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        const groupIds = [...new Set(memSnap.docs.map((d) => d.data()[GROUP_MEMBERS_FIELDS.GROUP_ID]))];
        if (groupIds.length > 0) {
          const groupSnaps = await Promise.all(
            groupIds.map((id) => getDoc(doc(db, COLLECTIONS.GROUPS, id)))
          );
          if (cancelled) return;
          const gMap = {};
          groupSnaps.forEach((snap, i) => {
            if (snap.exists()) gMap[groupIds[i]] = { id: snap.id, ...snap.data() };
          });
          setGroupsMap(gMap);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Errore nel caricamento del profilo.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const globalStats = useMemo(() => {
    const groupsCount = memberships.length;
    let totalPlayed = 0;
    let totalWon = 0;
    let maxRating = 0;
    ratings.forEach((r) => {
      const played = r[RATINGS_FIELDS.MATCHES_PLAYED] ?? 0;
      const wins = r[RATINGS_FIELDS.WINS] ?? 0;
      const pts = r[RATINGS_FIELDS.POINTS] ?? 0;
      totalPlayed += played;
      totalWon += wins;
      if (pts > maxRating) maxRating = pts;
    });
    const winRate = totalPlayed > 0 ? Math.round((totalWon / totalPlayed) * 1000) / 10 : 0;
    return { groupsCount, totalPlayed, totalWon, winRate, maxRating };
  }, [memberships.length, ratings]);

  const myGroupsWithRating = useMemo(() => {
    const byGroup = {};
    ratings.forEach((r) => {
      const gid = r[RATINGS_FIELDS.GROUP_ID];
      const pts = r[RATINGS_FIELDS.POINTS] ?? 0;
      if (!byGroup[gid] || pts > (byGroup[gid][RATINGS_FIELDS.POINTS] ?? 0)) byGroup[gid] = r;
    });
    return memberships
      .map((m) => {
        const gid = m[GROUP_MEMBERS_FIELDS.GROUP_ID];
        const group = groupsMap[gid];
        const rating = byGroup[gid];
        return {
          groupId: gid,
          groupName: group?.[GROUPS_FIELDS.NAME] ?? gid,
          rating: rating?.[RATINGS_FIELDS.POINTS] ?? null,
        };
      })
      .filter((g) => g.groupName);
  }, [memberships, ratings, groupsMap]);

  const handleAvatarClick = () => avatarRef.current?.click();

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const url = URL.createObjectURL(file);
    setAvatarPreview(url);
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!user?.uid) return;
    setSaveLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await updateUserProfile(user.uid, {
        [USERS_FIELDS.DISPLAY_NAME]: displayName.trim() || null,
      });
      setUserProfile((prev) => ({
        ...prev,
        [USERS_FIELDS.DISPLAY_NAME]: displayName.trim() || null,
      }));
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Errore nel salvataggio.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (e) {
      console.error('Errore durante il logout:', e);
    }
  };

  const photoURL = userProfile?.photoURL ?? null;
  const initials = getInitials(displayName || userProfile?.[USERS_FIELDS.DISPLAY_NAME], user?.email);
  const avatarSrc = avatarPreview || photoURL;
  const showName = (displayName || userProfile?.[USERS_FIELDS.DISPLAY_NAME] || user?.email || 'Utente').trim() || 'Utente';

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-royal border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl font-bold text-brand-ice">Il mio profilo</h1>

      {error && (
        <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-btn border border-win/60 bg-win/15 px-4 py-3 text-sm text-win">
          Modifiche salvate.
        </div>
      )}

      {/* Card Avatar */}
      <Card className="border-surface-muted bg-surface-card">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative h-20 w-20 shrink-0">
              <input
                ref={avatarRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="sr-only"
                aria-hidden="true"
                tabIndex={-1}
              />
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt=""
                  className="h-20 w-20 rounded-full object-cover"
                />
              ) : (
                <div
                  className="flex h-20 w-20 items-center justify-center rounded-full text-xl font-bold text-white"
                  style={{ backgroundColor: avatarColor(user?.uid) }}
                >
                  {initials}
                </div>
              )}
              <button
                type="button"
                onClick={handleAvatarClick}
                className="absolute bottom-0 right-0 flex h-9 w-9 items-center justify-center rounded-full bg-brand-royal text-brand-ice shadow-md transition-colors hover:bg-brand-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky"
                aria-label="Cambia foto profilo"
              >
                <IconCamera className="h-4 w-4" />
              </button>
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="font-display text-lg font-semibold text-brand-ice">{showName}</p>
              <p className="text-sm text-brand-ice/70">{user?.email ?? '—'}</p>
              {avatarPreview && (
                <p className="text-xs text-brand-ice/50">Caricamento foto in arrivo</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Informazioni profilo */}
      <Card className="border-surface-muted bg-surface-card">
        <CardContent className="space-y-4 p-4 sm:p-6">
          <h2 className="font-display text-lg font-semibold text-brand-ice">Informazioni profilo</h2>
          <div className="flex max-w-md flex-col gap-4">
            <div className="space-y-2">
              <Label htmlFor="profile-nickname">Nickname</Label>
              <Input
                id="profile-nickname"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-email">Email</Label>
              <Input
                id="profile-email"
                value={user?.email ?? ''}
                readOnly
                className="opacity-70"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-created">Data registrazione</Label>
              <Input
                id="profile-created"
                value={formatDate(userProfile?.[USERS_FIELDS.CREATED_AT])}
                readOnly
                className="opacity-70"
              />
            </div>
            <Button
              type="button"
              className="w-fit"
              onClick={handleSave}
              disabled={saveLoading}
            >
              {saveLoading ? 'Salvataggio...' : 'Salva modifiche'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Statistiche globali */}
      <Card className="border-surface-muted bg-surface-card">
        <CardContent className="space-y-4 p-4 sm:p-6">
          <h2 className="font-display text-lg font-semibold text-brand-ice">Statistiche globali</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-card bg-surface p-3 text-center">
              <p className="text-xs uppercase tracking-wide text-brand-ice/60">Gruppi</p>
              <p className="font-display text-2xl font-bold text-brand-ice">{globalStats.groupsCount}</p>
            </div>
            <div className="rounded-card bg-surface p-3 text-center">
              <p className="text-xs uppercase tracking-wide text-brand-ice/60">Set giocati</p>
              <p className="font-display text-2xl font-bold text-brand-ice">{globalStats.totalPlayed}</p>
            </div>
            <div className="rounded-card bg-surface p-3 text-center">
              <p className="text-xs uppercase tracking-wide text-brand-ice/60">Set vinti</p>
              <p className="font-display text-2xl font-bold text-brand-ice">{globalStats.totalWon}</p>
            </div>
            <div className="rounded-card bg-surface p-3 text-center">
              <p className="text-xs uppercase tracking-wide text-brand-ice/60">Win rate globale</p>
              <p className="font-display text-2xl font-bold text-win">{globalStats.winRate}%</p>
            </div>
            <div className="rounded-card bg-surface p-3 text-center">
              <p className="text-xs uppercase tracking-wide text-brand-ice/60">Rating più alto</p>
              <p className="font-display text-2xl font-bold text-gold">{globalStats.maxRating}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* I miei gruppi */}
      <Card className="border-surface-muted bg-surface-card">
        <CardContent className="space-y-3 p-4 sm:p-6">
          <h2 className="font-display text-lg font-semibold text-brand-ice">I miei gruppi</h2>
          {myGroupsWithRating.length === 0 ? (
            <p className="text-sm text-brand-ice/60">Non sei ancora in nessun gruppo.</p>
          ) : (
            <ul className="divide-y divide-surface-muted">
              {myGroupsWithRating.map(({ groupId, groupName, rating }) => (
                <li key={groupId}>
                  <button
                    type="button"
                    onClick={() => navigate(`/groups/${groupId}`)}
                    className="flex w-full items-center justify-between gap-3 py-3 text-left transition-colors hover:bg-surface-muted/30"
                  >
                    <span className="font-medium text-brand-ice">{groupName}</span>
                    {rating != null ? (
                      <span className="shrink-0 font-display text-lg font-semibold text-gold">{rating}</span>
                    ) : (
                      <span className="shrink-0 text-sm text-brand-ice/50">—</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="pt-2">
        <Button type="button" variant="destructive" onClick={handleLogout}>
          Esci
        </Button>
      </div>
    </div>
  );
}
